import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Button,
  Alert,
} from '@mui/material';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// i18n
import { useI18n } from '../i18n/I18nProvider';
import critereI18n from '../i18n/pages/criterepage.i18n';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';
const BLUE_BG = 'rgba(11,95,255,0.08)';

const Strong = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 800 }}>{children}</Box>
);

export default function CriterePage() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('critere', critereI18n); }, [register]);

  return (
    <>
      <Navbar />

      {/* HERO */}
      <Box sx={{ bgcolor: BLUE, color: '#fff', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: 26, md: 36 },
              lineHeight: 1.15,
              mb: 1,
            }}
          >
            {t('critere.hero.title')}
          </Typography>
          <Typography sx={{ opacity: 0.95, fontSize: { xs: 15.5, md: 17 } }}>
            {t('critere.hero.desc')}
          </Typography>
          <Alert
            severity="warning"
            sx={{
              mt: 2.5,
              maxWidth: 760,
              bgcolor: '#fff8e1',
              color: '#7a4b00',
              border: '1px solid #ffd36a',
              borderRadius: 2.5,
              fontWeight: 900,
              fontSize: { xs: 15, md: 17 },
            }}
          >
            {t('critere.dialog.originals')}
          </Alert>
        </Container>
      </Box>

      {/* INTRO */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              border: '1px solid #e8efe9',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip label={t('critere.intro.badge')} sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 800 }} />
              <Typography sx={{ fontWeight: 800 }}>{t('critere.intro.title')}</Typography>
            </Stack>

            <Stack spacing={1.25} sx={{ color: 'text.secondary' }}>
              {/* p1 */}
              <Typography>
                <Strong>{t('critere.intro.p1Strong')}</Strong>
                {' — '}
                {t('critere.intro.p1Rest')}
              </Typography>

              {/* p2 */}
              <Typography>
                <Strong>{t('critere.intro.p2Strong')}</Strong>
                {' — '}
                {t('critere.intro.p2Rest')}
              </Typography>
            </Stack>

            <Alert icon={false} sx={{ mt: 2, bgcolor: BLUE_BG, color: BLUE, borderRadius: 2 }}>
              {t('critere.intro.alert')}
            </Alert>
          </Paper>
        </Container>
      </Box>

      {/* EXEMPLES / EXPLICATIONS */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 4 } }}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, borderColor: '#e8efe9' }}
          >
            <Typography sx={{ fontWeight: 900, mb: 1 }}>
              {t('critere.examples.title')}
            </Typography>

            {/* Verre trop fissuré */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('critere.examples.cracks.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('critere.examples.cracks.bodyPrefix')}{' '}
                <Strong>{t('critere.examples.cracks.bodyStrong')}</Strong>
                {t('critere.examples.cracks.bodySuffix')}
              </Typography>
            </Box>

            {/* Taches / luminosité */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('critere.examples.spots.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('critere.examples.spots.bodyPrefix')}{' '}
                <Strong>{t('critere.examples.spots.onlyWord')}</Strong>{' '}
                {t('critere.examples.spots.middle')}{' '}
                <Strong>{t('critere.examples.spots.perfectDisplay')}</Strong>
                {t('critere.examples.spots.bodySuffix')}
              </Typography>
            </Box>

            {/* Test tactile */}
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('critere.examples.touch.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('critere.examples.touch.bodyPrefix')}{' '}
                <Strong>{t('critere.examples.touch.seriesStrong')}</Strong>
                {t('critere.examples.touch.bodySuffix')}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* RAPPEL COURT */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              border: '1px solid #e9f2ea',
              background: BLUE_BG,
            }}
          >
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 900, color: BLUE }}>
                {t('critere.recap.title')}
              </Typography>
              <Stack spacing={0.75} sx={{ color: 'text.secondary' }}>
                <Typography>• {t('critere.recap.i1')}</Typography>
                <Typography>• {t('critere.recap.i2')}</Typography>
                <Typography>• {t('critere.recap.i3')}</Typography>
                <Typography>• {t('critere.recap.i4')}</Typography>
                <Typography>• {t('critere.recap.i5')}</Typography>
                <Typography>• {t('critere.recap.i6')}</Typography>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  href="/tarifs"
                  variant="contained"
                  sx={{
                    bgcolor: BLUE,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': { backgroundColor: BLUE_HOVER },
                  }}
                >
                  {t('critere.recap.ctaTarifs')}
                </Button>
                <Button href="/envoi-securise" sx={{ textTransform: 'none' }}>
                  {t('critere.recap.ctaEnvoi')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
